/**
 * Global Keyboard Shortcuts Hook
 * Implements MATLAB-style session management shortcuts.
 *
 * Ctrl+T        — New session
 * Ctrl+W        — Close current session
 * Ctrl+Tab      — Next session
 * Ctrl+Shift+Tab — Previous session
 * Ctrl+1..9     — Switch to session N
 * Ctrl+Shift+D  — Duplicate current session
 * F2            — Rename current session
 * Ctrl+Shift+A  — Toggle archive sidebar
 * Ctrl+Shift+F  — Focus search
 * Ctrl+L        — Clear session history
 * Ctrl+Shift+N  — Reset environment
 */

import { useEffect } from "react";
import { useCalculatorStore } from "../store/calculatorStore";

export function useKeyboardShortcuts() {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const state = useCalculatorStore.getState();
      const {
        sessions,
        activeSessionId,
        createSession,
        deleteSession,
        switchSession,
        duplicateSession,
        clearSessionHistory,
        resetEnvironment,
        setShowArchivedSessions,
        showArchivedSessions,
      } = state;

      const visibleSessions = sessions
        .filter((s) => !s.isArchived)
        .sort((a, b) => a.position - b.position);

      const ctrl = e.ctrlKey || e.metaKey;
      const shift = e.shiftKey;

      // Ctrl+T — New session
      if (ctrl && !shift && e.key === "t") {
        e.preventDefault();
        createSession();
        return;
      }

      // Ctrl+W — Close current session
      if (ctrl && !shift && e.key === "w") {
        e.preventDefault();
        if (activeSessionId && visibleSessions.length > 1) {
          deleteSession(activeSessionId);
        }
        return;
      }

      // Ctrl+Tab — Next session
      if (ctrl && !shift && e.key === "Tab") {
        e.preventDefault();
        if (activeSessionId && visibleSessions.length > 1) {
          const currentIdx = visibleSessions.findIndex(
            (s) => s.id === activeSessionId,
          );
          const nextIdx = (currentIdx + 1) % visibleSessions.length;
          switchSession(visibleSessions[nextIdx].id);
        }
        return;
      }

      // Ctrl+Shift+Tab — Previous session
      if (ctrl && shift && e.key === "Tab") {
        e.preventDefault();
        if (activeSessionId && visibleSessions.length > 1) {
          const currentIdx = visibleSessions.findIndex(
            (s) => s.id === activeSessionId,
          );
          const prevIdx =
            (currentIdx - 1 + visibleSessions.length) % visibleSessions.length;
          switchSession(visibleSessions[prevIdx].id);
        }
        return;
      }

      // Ctrl+1..9 — Switch to session N
      if (ctrl && !shift && e.key >= "1" && e.key <= "9") {
        e.preventDefault();
        const idx = parseInt(e.key, 10) - 1;
        if (idx < visibleSessions.length) {
          switchSession(visibleSessions[idx].id);
        }
        return;
      }

      // Ctrl+Shift+D — Duplicate current session
      if (ctrl && shift && (e.key === "D" || e.key === "d")) {
        e.preventDefault();
        if (activeSessionId) {
          duplicateSession(activeSessionId);
        }
        return;
      }

      // F2 — Rename current session (dispatch custom event)
      if (e.key === "F2" && !ctrl && !shift) {
        e.preventDefault();
        window.dispatchEvent(
          new CustomEvent("quanta-rename-session", {
            detail: { sessionId: activeSessionId },
          }),
        );
        return;
      }

      // Ctrl+Shift+A — Toggle archived sessions sidebar
      if (ctrl && shift && (e.key === "A" || e.key === "a")) {
        e.preventDefault();
        setShowArchivedSessions(!showArchivedSessions);
        return;
      }

      // Ctrl+Shift+F or Ctrl+F — Focus search
      if (ctrl && (e.key === "F" || e.key === "f")) {
        e.preventDefault();
        const searchInput = document.querySelector(
          ".search-input",
        ) as HTMLInputElement | null;
        searchInput?.focus();
        return;
      }

      // Ctrl+L — Clear session history
      if (ctrl && !shift && e.key === "l") {
        e.preventDefault();
        clearSessionHistory();
        return;
      }

      // Ctrl+Shift+N — Reset environment
      if (ctrl && shift && (e.key === "N" || e.key === "n")) {
        e.preventDefault();
        resetEnvironment();
        return;
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
}
